import {
    isHTMLElement,
    createElement,
    getNextFocusableElement,
    setFocus,
    randomNumGenerator,
    isObject,
} from "../../../core/js/utilities";
import { dispatchEvent, renderState } from "../../../core/js/state-machine";
import { ddsSpace, prefix } from "../../../core/design-tokens/meta";
import { chevronLeft } from "../../../core/js/helpers/built-in-icons";
import {
    componentName,
    componentClasses,
    views,
    eventMap,
} from "../../../core/components/breadcrumb/breadcrumb";

export default function Breadcrumb(element, config = {}) {
    if (!isHTMLElement(element)) return console.error(`${componentName} missing element`);
    if (element[componentName]) return console.warn(`${componentName} already initialized`);
    if (!isObject(config)) config = {};

    /**
     * Expands breadcrumb links
     * @returns
     */
    const expand = () => {
        renderState(views.expandButton.hidden, expandButton.querySelector("a"));
        renderState(views.breadcrumbList.expanded, breadcrumbList);
        dispatchEvent(eventMap.expanded, element);
    };

    /**
     * Expands breadcrumb links
     * @returns
     */
    const collapse = () => {
        // TruncateLinks();
        createExpandButton();
        renderState(views.breadcrumbList.collapsed, breadcrumbList);
        dispatchEvent(eventMap.collapsed, element);
    };

    /**
     * Insert an expand button into our component that can be triggered to expand all our links and
     * remove any truncation
     */
    const createExpandButton = () => {
        // Our aria-label is customized to the number of links we have. We check html first, then
        // config, then provide a default if nothing is set.
        const htmlProvidedLabel =
            element.hasAttribute("data-button-label") && element.getAttribute("data-button-label");
        const configProvidedLabel =
            Object.prototype.hasOwnProperty.call(config, "buttonLabel") && config.buttonLabel;
        const buttonAriaLabel =
            htmlProvidedLabel ||
            configProvidedLabel ||
            `show ${Math.max(breadcrumbLinks.length - 5, 0)} more navigation levels`;

        // Create our expand button list element.
        expandButton = createElement("li", {
            class: componentClasses.listItem,
        });

        // We need to create an anchor that we can style and apply focus to and get the right amount
        // of padding and styling inherited.
        const anchor = createElement("a", {
            class: componentClasses.button,
            html: "...",
            tabIndex: "0",
            "aria-expanded": "false",
            "aria-controls": breadcrumbId,
            "aria-label": buttonAriaLabel,
            // We aren't really a link, but a button.
            role: "button",
        });

        // Attach the anchor as a child of the expandButton.
        expandButton.appendChild(anchor);

        // Use our parent OL to insert our button
        const breadcrumbOl = element.querySelector("ol");
        breadcrumbOl.insertBefore(expandButton, breadcrumbOl.children[1]);

        // Add our event listener for when we want to expand.
        expandButton.addEventListener("click", expand, false);

        // Add our event listener for focus when we want to expand via keyboard.
        expandButton.addEventListener(
            "keydown",
            (e) => {
                if (e.key === "Enter" || e.key === " ") {
                    expand();
                    setFocus(getNextFocusableElement(breadcrumbLinks[0]));
                }
            },
            false
        );
    };

    const createMobileBackIcon = () => {
        breadcrumbLinks[breadcrumbLinks.length - 2].insertAdjacentHTML(
            "afterbegin",
            chevronLeft({ class: `${prefix}breadcrumb__mobile-icon` })
        );
    };

    /**
     * Used to cleanup the component, removing references and listeners.
     */
    const dispose = () => {
        if (expandButton) {
            expandButton.removeEventListener("click", expand, false);
            expandButton.removeEventListener("keydown", expand, false);
        }
        element[componentName] = null;
        element = null;
    };

    /**
     * If the user wants to pass their own id on their ol, they should be able to. If not, we should
     * provide a dynamically generated id for them.
     * @returns {string} id for the breadcrumb
     */
    const getBreadcrumbId = () => {
        return breadcrumbList.hasAttribute("id")
            ? breadcrumbList.getAttribute("id")
            : `${ddsSpace}-breadcrumb-${randomNumGenerator()}`;
    };

    const breadcrumbLinks = Array.from(element.querySelectorAll(`.${componentClasses.listItem}`));
    const breadcrumbList = element.querySelector(`.${componentClasses.list}`);
    const minItems = 4;
    const breadcrumbId = getBreadcrumbId();
    let expandButton;

    breadcrumbList.setAttribute("id", breadcrumbId);

    if (breadcrumbLinks.length >= 3) {
        createMobileBackIcon();
    }

    /**
     * The last link in the breadcrumb should be rendered with aria current page.
     */
    renderState(views.breadcrumbItem.active, breadcrumbLinks[breadcrumbLinks.length - 1]);

    // If there is not more than 2 links in the breadcrumb, we need to not render.
    if (breadcrumbLinks.length <= minItems) {
        return console.warn(
            `There are not enough breadcrumb links. Must have at least ${minItems + 1}.`
        );
    }

    // If we have 4 or more links in our breadcrumb, we want to hide all the belly fat and
    // replace it with a expand button, so the user can expand it if they want.
    if (breadcrumbLinks.length > minItems) {
        collapse();
    }

    // *****************************************************
    // Public API
    // *****************************************************
    const api = {
        element,
        expand,
        collapse,
        dispose,
    };

    element[componentName] = api;

    // Return ourselves incase we are captured in js.
    return api;
}
